import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import styled from 'styled-components';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import isEqual from '@userzoomlibraries/toolkit-js-utils/is-equal';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useComparePrevious from '@userzoomlibraries/toolkit-react-utils/use-compare-previous';
import { I as Item, R as RadioContext } from './index.styled2.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["className", "name", "invalid", "defaultValue", "value", "disabled", "placement", "onChange", "onFocus", "onBlur", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Radio$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      name = _ref.name,
      invalid = _ref.invalid,
      defaultValue = _ref.defaultValue,
      value = _ref.value,
      disabled = _ref.disabled,
      placement = _ref.placement,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      children = _ref.children,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps, {
    excludedAttributes: ['aria-invalid']
  });
  var dataAttributes = useDataAttributes(otherProps);
  var isControlledRadio = useMemo(function () {
    if (isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    if (!isDefinedAndNotNull(defaultValue) && isDefinedAndNotNull(value)) {
      return true;
    }

    if (!isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    console.error("Warning: ".concat(Radio$1.displayName, " contains an input with both value and defaultValue props.\n      Input elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both).\n      Decide between using a controlled or uncontrolled input element and remove one of these props. More info: https://reactjs.org/link/controlled-components"));
    return false;
  }, [useComparePrevious(value), useComparePrevious(defaultValue)]);

  var _useState = useState({
    name: name,
    invalid: invalid,
    selectedValue: defaultValue || value,
    isControlledRadio: isControlledRadio
  }),
      _useState2 = _slicedToArray(_useState, 2),
      radioInfo = _useState2[0],
      setRadioInfo = _useState2[1];

  var onUpdateSelection = useCallback(function (_ref2) {
    var radioId = _ref2.radioId,
        radioRefs = _ref2.radioRefs;

    var _radioRefs$find = radioRefs.find(function (each) {
      return each.id === radioId;
    }),
        radioDisabled = _radioRefs$find.disabled,
        radioValue = _radioRefs$find.value,
        focusElement = _radioRefs$find.ref.current;

    var isSelected = isEqual(radioValue, radioInfo.selectedValue);

    if (!disabled && !radioDisabled && !isSelected) {
      // We need to call click method to manage properly the internal focus
      focusElement.click();

      if (!isControlledRadio) {
        setRadioInfo(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            selectedValue: radioValue,
            focusedItem: radioId
          });
        });
      }

      onChange({
        value: radioValue
      });
    }
  }, [disabled, isControlledRadio, radioInfo.selectedValue, onChange]);
  var onPreviousArrowPressed = useCallback(function (_ref3) {
    var radioId = _ref3.radioId,
        radioRefs = _ref3.radioRefs;
    var indexClicked = radioRefs.findIndex(function (each) {
      return each.id === radioId;
    });
    var previousIndex = indexClicked - 1;

    if (previousIndex < 0) {
      previousIndex = radioRefs.length - 1;
    }

    var _radioRefs$find2 = radioRefs.find(function (each, index) {
      return index === previousIndex;
    }),
        previousRadioId = _radioRefs$find2.id,
        radioDisabled = _radioRefs$find2.disabled,
        focusElement = _radioRefs$find2.ref.current,
        previousValue = _radioRefs$find2.value;

    if (!radioDisabled) {
      // We need to call click method to manage properly the internal focus
      focusElement.click();

      if (!isControlledRadio) {
        setRadioInfo(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            selectedValue: previousValue
          });
        });
      }

      onChange({
        value: previousValue
      });
    } else {
      onPreviousArrowPressed({
        radioId: previousRadioId,
        radioRefs: radioRefs
      });
    }
  }, [isControlledRadio, onChange]);
  var onNextArrowPressed = useCallback(function (_ref4) {
    var radioId = _ref4.radioId,
        radioRefs = _ref4.radioRefs;
    var indexClicked = radioRefs.findIndex(function (each) {
      return each.id === radioId;
    });
    var nextIndex = indexClicked + 1;

    if (nextIndex > radioRefs.length - 1) {
      nextIndex = 0;
    }

    var _radioRefs$find3 = radioRefs.find(function (each, index) {
      return index === nextIndex;
    }),
        nextRadioId = _radioRefs$find3.id,
        radioDisabled = _radioRefs$find3.disabled,
        focusElement = _radioRefs$find3.ref.current,
        nextValue = _radioRefs$find3.value;

    if (!radioDisabled) {
      // We need to call click method to manage properly the internal focus
      focusElement.click();

      if (!isControlledRadio) {
        setRadioInfo(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            selectedValue: nextValue
          });
        });
      }

      onChange({
        value: nextValue
      });
    } else {
      onNextArrowPressed({
        radioId: nextRadioId,
        radioRefs: radioRefs
      });
    }
  }, [isControlledRadio, onChange]);
  useEffect(function () {
    setRadioInfo(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        name: name,
        placement: placement,
        invalid: invalid,
        disabled: disabled,
        onPreviousArrowPressed: onPreviousArrowPressed,
        onNextArrowPressed: onNextArrowPressed,
        onUpdateSelection: onUpdateSelection,
        onFocus: onFocus,
        onBlur: onBlur
      });
    });
  }, [name, placement, invalid, disabled, onPreviousArrowPressed, onNextArrowPressed, onUpdateSelection, onFocus, onBlur]);
  useEffect(function () {
    setRadioInfo(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        isControlledRadio: isControlledRadio,
        selectedValue: defaultValue || value
      });
    });
  }, [isControlledRadio, defaultValue, value]);
  return /*#__PURE__*/jsx(RadioContext.Provider, {
    value: {
      radioInfo: radioInfo,
      setRadioInfo: setRadioInfo
    },
    children: /*#__PURE__*/jsx("div", _objectSpread(_objectSpread(_objectSpread({
      tabIndex: "-1",
      ref: ref,
      role: "radiogroup",
      className: "gz-radio-group ".concat(className),
      "aria-invalid": radioInfo.invalid // eslint-disable-next-line react/jsx-props-no-spreading

    }, ariaAttributes), dataAttributes), {}, {
      children: children
    }))
  });
});
Radio$1.COMPONENT_ID = 'Gearz.Radio';
Radio$1.displayName = Radio$1.COMPONENT_ID;
Radio$1.Item = Item;
Radio$1.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  name: PropTypes.stringWithDescription('Name of the radio.'),
  invalid: PropTypes.boolWithDescription('Flag to make the radio invalid.'),
  disabled: PropTypes.boolWithDescription('Flag to make the radio disable.'),
  defaultValue: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.number, PropTypes.bool], 'Initial value of the radio. (uncontrolled)'),
  value: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.number, PropTypes.bool], 'Selected value of the radio. (controlled)'),
  placement: PropTypes.oneOfWithDescription(['top', 'right', 'bottom', 'left'], 'Placement of the label of the radio.'),
  onChange: PropTypes.funcWithDescription('Callback called when the value of the radio has changed.'),
  onFocus: PropTypes.funcWithDescription('Callback called when focus event is triggered.'),
  onBlur: PropTypes.funcWithDescription('Callback called when blur event is triggered.'),
  children: PropTypes.nodeWithDescription('Content of the radio.').isRequired
};
Radio$1.defaultProps = {
  className: '',
  name: null,
  defaultValue: undefined,
  value: undefined,
  invalid: false,
  disabled: false,
  placement: 'right',
  onChange: noop,
  onFocus: noop,
  onBlur: noop
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Radio = styled(Radio$1).withConfig({
  componentId: "gz-radio-1673535260696__sc-gcxkwc-0"
})(["outline:none;position:relative;touch-action:none;user-select:none;.gz-radio{&:not(:last-child){margin-right:6px;}}", ""], function (props) {
  return getThemeByProps({
    componentId: Radio$1.COMPONENT_ID,
    props: props
  });
});
Radio.displayName = Radio$1.displayName;
Radio.propTypes = Radio$1.propTypes;
Radio.defaultProps = Radio$1.defaultProps;

export { Radio as R };
